import { useEffect, useRef } from 'react'
import { Trash2, Copy, AlignLeft, AlignCenter, AlignRight, PlusCircle, StopCircle } from 'lucide-react'
import type { ContextMenuState } from './types'

interface Props {
  state: ContextMenuState
  onClose: () => void
  onDelete: () => void
  onDuplicate: (id: string) => void
  onAlign: (id: string, alignment: 'left' | 'center' | 'right') => void
  onAddActivity: () => void
  onAddEnd: () => void
}

export default function ContextMenu({
  state,
  onClose,
  onDelete,
  onDuplicate,
  onAlign,
  onAddActivity,
  onAddEnd,
}: Props) {
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!state.visible) return
    const handler = () => onClose()
    window.addEventListener('click', handler)
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('click', handler)
      window.removeEventListener('resize', handler)
    }
  }, [state.visible, onClose])

  // Ensure menu stays within viewport
  useEffect(() => {
    if (!state.visible || !ref.current) return
    const menu = ref.current
    const rect = menu.getBoundingClientRect()
    if (rect.right > window.innerWidth) {
      menu.style.left = `${state.x - rect.width}px`
    }
    if (rect.bottom > window.innerHeight) {
      menu.style.top = `${state.y - rect.height}px`
    }
  }, [state])

  if (!state.visible) return null

  const a = state.activity

  return (
    <div
      ref={ref}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed',
        left: state.x,
        top: state.y,
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        minWidth: 160,
        zIndex: 300,
        overflow: 'hidden',
        fontSize: '0.8625rem',
      }}
    >
      {a ? (
        <>
          <MenuItem icon={<Trash2 size={13} />} label="Delete" onClick={() => { onDelete(); onClose() }} />
          <MenuItem icon={<Copy size={13} />} label="Duplicate" onClick={() => { onDuplicate(a.id); onClose() }} />
          <div style={{ height: 1, background: 'var(--color-border)' }} />
          <MenuItem icon={<AlignLeft size={13} />} label="Align Left" onClick={() => { onAlign(a.id, 'left'); onClose() }} />
          <MenuItem icon={<AlignCenter size={13} />} label="Align Center" onClick={() => { onAlign(a.id, 'center'); onClose() }} />
          <MenuItem icon={<AlignRight size={13} />} label="Align Right" onClick={() => { onAlign(a.id, 'right'); onClose() }} />
        </>
      ) : (
        <>
          <MenuItem icon={<PlusCircle size={13} />} label="Add Activity" onClick={() => { onAddActivity(); onClose() }} />
          <MenuItem icon={<StopCircle size={13} />} label="Add End" onClick={() => { onAddEnd(); onClose() }} />
        </>
      )}
    </div>
  )
}

function MenuItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '7px 12px',
        background: 'transparent', border: 'none',
        color: 'var(--color-text)', cursor: 'pointer',
        fontSize: '0.8625rem', textAlign: 'left',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      {icon}
      {label}
    </button>
  )
}
